import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Coins, Crown, Globe, Loader2, Swords, Trophy, Users, Share2, Volume2, VolumeX,
} from "lucide-react";
import { plyxApi as api } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { millionsAudio } from "@/lib/millionsAudio";
import { toast } from "sonner";

/**
 * PlyxMillions — `/plyx/millions`
 * Millionaire-style trivia ladder: solo climbs for points and 1v1
 * coin-stake duels with AI-generated questions per player.
 */

const fmt = (n) => (n >= 1000000 ? `${n / 1000000}M` : n >= 1000 ? `${(n / 1000).toLocaleString()}K` : String(n));

const LETTERS = ["A", "B", "C", "D"];

const Ladder = ({ ladder, safeRungs, rung }) => (
  <div className="flex flex-col-reverse gap-[3px]" data-testid="millions-ladder">
    {ladder.map((amt, i) => {
      const n = i + 1;
      const reached = rung >= n;
      const current = rung + 1 === n;
      const safe = safeRungs.includes(n);
      return (
        <div
          key={n}
          className={`flex items-center justify-between px-2.5 py-[3px] rounded-md text-[11px] font-bold transition-colors ${
            current ? "bg-[#FFB42D] text-black" :
            reached ? "bg-[#00E0C7]/20 text-[#00E0C7]" :
            safe ? "bg-white/10 text-white/80" : "bg-white/[0.04] text-white/40"
          }`}
        >
          <span>{n}</span>
          <span>{safe ? "🛡 " : ""}{fmt(amt)}</span>
        </div>
      );
    })}
  </div>
);

const GiftBar = ({ sessionId, gifts, onSent }) => {
  const [busy, setBusy] = useState(null);
  const send = async (g) => {
    setBusy(g.code);
    try {
      const r = await api.post(`/plyx/millions/watch/${sessionId}/gift`, { gift_code: g.code });
      toast.success(`${g.emoji} ${g.label} sent!`);
      onSent && onSent(r.data?.gift);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send gift");
    } finally { setBusy(null); }
  };
  return (
    <div className="flex gap-2 flex-wrap justify-center" data-testid="millions-gift-bar">
      {gifts.map((g) => (
        <button
          key={g.code}
          type="button"
          onClick={() => send(g)}
          disabled={busy === g.code}
          data-testid={`millions-gift-${g.code}`}
          className="px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold disabled:opacity-50 transition-colors"
        >
          {g.emoji} {g.label} · {g.coins}
        </button>
      ))}
    </div>
  );
};

export default function PlyxMillions() {
  const { user } = usePlyxAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [view, setView] = useState("hub"); // hub | starting | game | result | duel-new
  const [category, setCategory] = useState("general");
  const [state, setState] = useState(null); // game session state
  const [verdict, setVerdict] = useState(null); // {choice, correctIndex, result}
  const [locking, setLocking] = useState(false);
  const [duels, setDuels] = useState([]);
  const [duelForm, setDuelForm] = useState({ handle: "", stake: 50 });
  const [duelBusy, setDuelBusy] = useState(false);
  const [battles, setBattles] = useState({ mine: [], open: [] });
  const [battleForm, setBattleForm] = useState({ teamSize: 2, stake: 50, teamA: "Team A", teamB: "Team B" });
  const [battleBusy, setBattleBusy] = useState(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("plyx_millions_muted") !== "1");
  const [timeLeft, setTimeLeft] = useState(60);
  const timerRef = useRef(null);
  const location = useLocation();
  const autoStartedRef = useRef(false);

  const loadMeta = useCallback(async () => {
    try {
      const r = await api.get("/plyx/millions/meta");
      setMeta(r.data);
    } catch { /* ignore */ }
  }, []);
  const loadDuels = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/plyx/millions/duels");
      setDuels(r.data?.duels || []);
    } catch { /* ignore */ }
  }, [user]);

  const loadBattles = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/plyx/millions/battles");
      setBattles({ mine: r.data?.mine || [], open: r.data?.open || [] });
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { loadMeta(); loadDuels(); loadBattles(); }, [loadMeta, loadDuels, loadBattles]);

  // Arriving from a battle page with ?battle=… — jump straight into the climb.
  useEffect(() => {
    const b = new URLSearchParams(location.search).get("battle");
    if (b && user && !autoStartedRef.current) {
      autoStartedRef.current = true;
      startGame(null, b);
    }
  }, [location.search, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // question timer — 60s per question; countdown ticks turn audible and the
  // stage pulses red in the final 10s; at 0 the climb auto-banks (cash out).
  const timeoutFnRef = useRef(null);
  useEffect(() => {
    clearInterval(timerRef.current);
    if (view === "game" && state?.question && !verdict) {
      setTimeLeft(60);
      timerRef.current = setInterval(() => {
        setTimeLeft((t) => {
          const next = t - 1;
          if (next <= 10 && next > 0) millionsAudio.tick(next <= 5);
          if (next <= 0) {
            clearInterval(timerRef.current);
            if (timeoutFnRef.current) timeoutFnRef.current();
            return 0;
          }
          return next;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [view, state?.question?.number, verdict]);

  // suspense music follows the game screen; heartbeat speeds up as time drains
  useEffect(() => {
    if (view === "game" && state?.question) millionsAudio.startSuspense();
    else if (view !== "game") millionsAudio.stopMusic();
  }, [view, state?.question?.number]);
  useEffect(() => {
    if (view === "game") millionsAudio.setIntensity(timeLeft <= 10 ? 1 : timeLeft <= 25 ? 0.5 : 0.2);
  }, [timeLeft, view]);
  useEffect(() => () => millionsAudio.stopMusic(), []);

  const handleTimeout = async () => {
    if (locking || verdict) return;
    setLocking(true);
    millionsAudio.sfxWrong();
    try {
      const r = await api.post("/plyx/millions/answer", { session_id: state.session_id, choice: 0, timed_out: true });
      setVerdict({ choice: -1, correctIndex: r.data.correct_index, result: "wrong" });
      toast.error("⏰ Time's up — on to the next question!");
      const next = r.data.state;
      setTimeout(() => {
        setState(next);
        setVerdict(null);
        if (next.status !== "active") { setView("result"); loadMeta(); loadDuels(); }
      }, 1600);
    } catch { /* session may have ended */ } finally { setLocking(false); }
  };
  useEffect(() => { timeoutFnRef.current = handleTimeout; });

  const startGame = async (duelId, battleId) => {
    if (!user) { navigate("/plyx/login"); return; }
    millionsAudio.ensure();
    setView("starting");
    try {
      const r = await api.post("/plyx/millions/start", { category, duel_id: duelId || null, battle_id: battleId || null });
      setState(r.data);
      setVerdict(null);
      setView(r.data.status === "active" ? "game" : "result");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the game");
      setView("hub");
    }
  };

  const answer = async (choice) => {
    if (locking || verdict) return;
    setLocking(true);
    try {
      const r = await api.post("/plyx/millions/answer", { session_id: state.session_id, choice });
      setVerdict({ choice, correctIndex: r.data.correct_index, result: r.data.result });
      if (r.data.result === "correct") millionsAudio.sfxCorrect(); else millionsAudio.sfxWrong();
      const next = r.data.state;
      setTimeout(() => {
        setState(next);
        setVerdict(null);
        if (next.status !== "active") {
          if (next.status === "won") millionsAudio.sfxWin();
          setView("result"); loadMeta(); loadDuels();
        }
      }, 1600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Answer failed");
    } finally { setLocking(false); }
  };

  const [lifelineBusy, setLifelineBusy] = useState(false);
  const fireLifeline = async (lifeline) => {
    if (lifelineBusy) return;
    setLifelineBusy(true);
    try {
      const r = await api.post("/plyx/millions/lifeline", { session_id: state.session_id, lifeline });
      setState(r.data.state);
      toast.message(lifeline === "fifty" ? "50:50 — two wrong answers removed" : "Question skipped");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lifeline failed");
    } finally { setLifelineBusy(false); }
  };

  const createDuel = async () => {
    if (!duelForm.handle.trim()) { toast.error("Enter the player's @handle"); return; }
    setDuelBusy(true);
    try {
      await api.post("/plyx/millions/duel", {
        opponent_handle: duelForm.handle.trim(),
        stake_coins: Number(duelForm.stake) || 0,
        category,
      });
      toast.success("Challenge sent! Waiting for them to accept.");
      setView("hub");
      loadDuels(); loadMeta();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the challenge");
    } finally { setDuelBusy(false); }
  };

  const duelAction = async (duel, action) => {
    try {
      await api.post(`/plyx/millions/duel/${duel.duel_id}/${action}`);
      toast.success(action === "accept" ? "Challenge accepted — play your climb!" : "Challenge declined");
      loadDuels(); loadMeta();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const createBattle = async () => {
    setBattleBusy(true);
    try {
      const r = await api.post("/plyx/millions/battle", {
        team_size: Number(battleForm.teamSize),
        stake_coins: Number(battleForm.stake) || 0,
        category,
        team_a_name: battleForm.teamA,
        team_b_name: battleForm.teamB,
      });
      toast.success("Battle created — share the link so players can join!");
      navigate(`/plyx/millions/battle/${r.data.battle_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the battle");
    } finally { setBattleBusy(false); }
  };

  const shareGame = () => {
    const url = `${window.location.origin}/plyx/millions/watch/${state.session_id}`;
    if (navigator.share) navigator.share({ title: "Cheer me on in Plyx Millions!", url }).catch(() => {});
    else { navigator.clipboard?.writeText(url); toast.success("Watch link copied — share it with your fans!"); }
  };

  if (!meta) {
    return (
      <div className="min-h-screen millions-stage text-white flex items-center justify-center" data-testid="millions-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-2"/> Loading Plyx Millions…
      </div>
    );
  }

  // ───────────────────────── GAME VIEW ─────────────────────────
  if (view === "starting") {
    return (
      <div className="min-h-screen millions-stage text-white flex flex-col items-center justify-center gap-4 px-6" data-testid="millions-starting">
        <div className="text-5xl animate-bounce">💰</div>
        <div className="text-xl font-extrabold">Building your ladder…</div>
        <p className="text-sm text-white/60 text-center">Fresh questions are being generated just for you. No two climbs are ever the same.</p>
        <Loader2 className="w-6 h-6 animate-spin text-[#FFB42D]"/>
      </div>
    );
  }

  if (view === "game" && state?.question) {
    const q = state.question;
    const danger = timeLeft <= 10 && !verdict;
    return (
      <div className={`min-h-screen millions-stage text-white pb-10 ${danger ? "millions-danger" : ""}`} data-testid="millions-game">
        <div className="max-w-3xl mx-auto px-4 pt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="px-4 py-1.5 rounded-full bg-[#FFB42D] text-black text-sm font-extrabold" data-testid="millions-score-chip">
              ✓ {state.rung}/{state.ladder.length}
            </div>
            <div className={`w-11 h-11 rounded-full border-2 flex items-center justify-center font-extrabold transition-transform ${danger ? "border-[#FF2D7A] text-[#FF2D7A] animate-pulse scale-110" : "border-white/30"}`} data-testid="millions-timer">
              {timeLeft}
            </div>
            <div className="flex gap-2">
              <button type="button" data-testid="millions-sound-toggle"
                title={soundOn ? "Mute music & sounds" : "Unmute music & sounds"}
                onClick={() => { millionsAudio.ensure(); const m = millionsAudio.toggleMuted(); setSoundOn(!m); }}
                className="p-2 rounded-full bg-white/10">
                {soundOn ? <Volume2 className="w-4 h-4"/> : <VolumeX className="w-4 h-4"/>}
              </button>
              <button type="button" onClick={shareGame} data-testid="millions-share-btn"
                className="p-2 rounded-full bg-white/10"><Share2 className="w-4 h-4"/></button>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-xs text-white/50 mb-1">{state.category_label} · Question {q.number} of {state.ladder.length} · <span className="text-[#FFB42D] font-bold">every correct answer counts</span></div>
              <div className="rounded-2xl millions-qcard p-5 mb-4">
                <p className="text-lg font-bold leading-snug" data-testid="millions-question">{q.q}</p>
              </div>
              <div className="grid grid-cols-1 gap-2.5">
                {q.options.map((opt, i) => {
                  const removed = (q.removed || []).includes(i);
                  let cls = "bg-white/[0.06] border-white/10 hover:border-[#FFB42D]/60";
                  if (verdict) {
                    if (i === verdict.correctIndex) cls = "bg-[#00E0C7]/20 border-[#00E0C7]";
                    else if (i === verdict.choice) cls = "bg-[#FF2D7A]/20 border-[#FF2D7A]";
                    else cls = "bg-white/[0.03] border-white/5 opacity-60";
                  }
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={removed || !!verdict || locking}
                      onClick={() => answer(i)}
                      data-testid={`millions-option-${i}`}
                      className={`text-left px-4 py-3.5 rounded-xl border font-semibold text-[15px] transition-colors disabled:cursor-not-allowed ${removed ? "opacity-20 line-through bg-white/[0.02] border-white/5" : cls}`}
                    >
                      <span className="text-[#FFB42D] font-extrabold mr-2">{LETTERS[i]}.</span>{opt}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-4">
                <button type="button" disabled={!state.lifelines.fifty || !!verdict} onClick={() => fireLifeline("fifty")}
                  data-testid="millions-lifeline-fifty"
                  className="flex-1 py-2.5 rounded-full bg-white/10 font-bold text-sm disabled:opacity-30">50 : 50</button>
                <button type="button" disabled={!state.lifelines.skip || !!verdict} onClick={() => fireLifeline("skip")}
                  data-testid="millions-lifeline-skip"
                  className="flex-1 py-2.5 rounded-full bg-white/10 font-bold text-sm disabled:opacity-30">⏭ Skip</button>
              </div>
            </div>
            <div className="hidden sm:block w-36 shrink-0">
              <Ladder ladder={state.ladder} safeRungs={state.safe_rungs} rung={state.rung}/>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === "result" && state) {
    const won = state.status === "won";
    const total = state.ladder?.length || 8;
    const score = Math.min(state.rung, total);
    return (
      <div className="min-h-screen millions-stage text-white flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="millions-result">
        <div className="text-6xl">{won ? "👑" : score >= total / 2 ? "💪" : "📚"}</div>
        <h1 className="text-3xl font-extrabold">
          {won ? "PERFECT RUN — MILLIONAIRE!" : "Game complete!"}
        </h1>
        <div className="text-5xl font-extrabold text-[#FFB42D]" data-testid="millions-final-score">
          {score}<span className="text-2xl text-white/50">/{total}</span>
        </div>
        <p className="text-white/70">
          {state.mode === "duel"
            ? `You scored ${score}/${total}. Your opponent's score decides the pot — check the duel board.`
            : state.mode === "battle"
            ? `You locked in ${score}/${total} for your team. Combined scores decide the pot.`
            : `You answered ${score} of ${total} correctly and banked ${fmt(state.banked_points || 0)} points on the ${state.category_label} ladder.`}
        </p>
        {state.golden && (
          <div className="px-3 py-1 rounded-full bg-[#FFB42D]/15 border border-[#FFB42D]/40 text-[#FFB42D] text-xs font-extrabold" data-testid="millions-golden-tag">
            🌟 Golden Hour — your points counted DOUBLE!
          </div>
        )}
        <div className="flex gap-3 mt-2">
          <button type="button" onClick={() => { setState(null); setView("hub"); loadMeta(); loadDuels(); }}
            data-testid="millions-back-hub"
            className="px-6 py-2.5 rounded-full bg-white/10 font-bold">Back to hub</button>
          {state.battle_id ? (
            <button type="button" onClick={() => navigate(`/plyx/millions/battle/${state.battle_id}`)}
              data-testid="millions-view-battle"
              className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-black font-extrabold">View battle</button>
          ) : (
            <button type="button" onClick={() => startGame(null)}
              data-testid="millions-play-again"
              className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-black font-extrabold">Play again</button>
          )}
        </div>
        <button type="button" onClick={shareGame} className="text-sm text-white/50 underline mt-1" data-testid="millions-share-result">
          Share your climb so fans can send gifts 🌸
        </button>
      </div>
    );
  }

  if (view === "duel-new") {
    return (
      <div className="min-h-screen millions-stage text-white pb-24" data-testid="millions-duel-new">
        <div className="max-w-xl mx-auto px-4 pt-6">
          <button type="button" onClick={() => setView("hub")} className="flex items-center gap-1 text-white/60 text-sm mb-4" data-testid="millions-duel-back">
            <ArrowLeft className="w-4 h-4"/> Back
          </button>
          <h1 className="text-2xl font-extrabold mb-1">Challenge a player ⚔️</h1>
          <p className="text-sm text-white/60 mb-5">You each stake the same coins. You both climb your own ladder of fresh AI questions — the higher climber takes the pot (10% platform fee, ties refunded).</p>
          <label className="block text-sm font-bold mb-1">Opponent @handle</label>
          <input
            value={duelForm.handle}
            onChange={(e) => setDuelForm((f) => ({ ...f, handle: e.target.value }))}
            placeholder="@handle"
            data-testid="millions-duel-handle-input"
            className="w-full px-4 py-3 rounded-xl bg-white/[0.06] border border-white/10 mb-4 outline-none focus:border-[#FFB42D]"
          />
          <label className="block text-sm font-bold mb-1">Stake (Plyx Coins)</label>
          <div className="flex gap-2 mb-2">
            {[50, 100, 500, 1000].map((v) => (
              <button key={v} type="button" onClick={() => setDuelForm((f) => ({ ...f, stake: v }))}
                data-testid={`millions-stake-${v}`}
                className={`px-4 py-2 rounded-full text-sm font-bold ${Number(duelForm.stake) === v ? "bg-[#FFB42D] text-black" : "bg-white/10"}`}>{v}</button>
            ))}
          </div>
          <input
            type="number" min={meta.min_stake} max={meta.max_stake}
            value={duelForm.stake}
            onChange={(e) => setDuelForm((f) => ({ ...f, stake: e.target.value }))}
            data-testid="millions-duel-stake-input"
            className="w-full px-4 py-3 rounded-xl bg-white/[0.06] border border-white/10 mb-1 outline-none focus:border-[#FFB42D]"
          />
          <p className="text-xs text-white/40 mb-4">Your balance: {meta.me?.coin_balance ?? 0} coins · <Link to="/plyx/wallet" className="underline">buy coins</Link></p>
          <label className="block text-sm font-bold mb-2">Category</label>
          <div className="flex flex-wrap gap-2 mb-6">
            {meta.categories.map((c) => (
              <button key={c.code} type="button" onClick={() => setCategory(c.code)}
                data-testid={`millions-duel-cat-${c.code}`}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold ${category === c.code ? "bg-[#FFB42D] text-black" : "bg-white/10"}`}>
                {c.emoji} {c.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={createDuel} disabled={duelBusy}
            data-testid="millions-duel-send"
            className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-black font-extrabold disabled:opacity-50">
            {duelBusy ? "Sending…" : `Send challenge · stake ${duelForm.stake} coins`}
          </button>
        </div>
      </div>
    );
  }

  if (view === "battle-new") {
    return (
      <div className="min-h-screen millions-stage text-white pb-24" data-testid="millions-battle-new">
        <div className="max-w-xl mx-auto px-4 pt-6">
          <button type="button" onClick={() => setView("hub")} className="flex items-center gap-1 text-white/60 text-sm mb-4" data-testid="millions-battle-back">
            <ArrowLeft className="w-4 h-4"/> Back
          </button>
          <h1 className="text-2xl font-extrabold mb-1">Create a team battle 🔥</h1>
          <p className="text-sm text-white/60 mb-5">Everyone stakes the same coins. Each player answers their own 8 questions — the team with the higher combined score splits the pot (10% platform fee, ties refunded). Share the battle link so players can join either side.</p>
          <label className="block text-sm font-bold mb-1">Players per team</label>
          <div className="flex gap-2 mb-4">
            {[2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setBattleForm((f) => ({ ...f, teamSize: n }))}
                data-testid={`millions-team-size-${n}`}
                className={`px-4 py-2 rounded-full text-sm font-bold ${battleForm.teamSize === n ? "bg-[#FF2D7A] text-white" : "bg-white/10"}`}>{n}v{n}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-sm font-bold mb-1">Your team&apos;s name</label>
              <input value={battleForm.teamA} maxLength={30}
                onChange={(e) => setBattleForm((f) => ({ ...f, teamA: e.target.value }))}
                data-testid="millions-team-a-input"
                className="w-full px-3 py-2.5 rounded-xl bg-white/[0.06] border border-white/10 outline-none focus:border-[#FF2D7A]"/>
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Rival team&apos;s name</label>
              <input value={battleForm.teamB} maxLength={30}
                onChange={(e) => setBattleForm((f) => ({ ...f, teamB: e.target.value }))}
                data-testid="millions-team-b-input"
                className="w-full px-3 py-2.5 rounded-xl bg-white/[0.06] border border-white/10 outline-none focus:border-[#FF2D7A]"/>
            </div>
          </div>
          <label className="block text-sm font-bold mb-1">Stake per player (Plyx Coins)</label>
          <div className="flex gap-2 mb-2">
            {[50, 100, 500, 1000].map((v) => (
              <button key={v} type="button" onClick={() => setBattleForm((f) => ({ ...f, stake: v }))}
                data-testid={`millions-battle-stake-${v}`}
                className={`px-4 py-2 rounded-full text-sm font-bold ${Number(battleForm.stake) === v ? "bg-[#FFB42D] text-black" : "bg-white/10"}`}>{v}</button>
            ))}
          </div>
          <input type="number" min={meta.min_stake} max={meta.max_stake} value={battleForm.stake}
            onChange={(e) => setBattleForm((f) => ({ ...f, stake: e.target.value }))}
            data-testid="millions-battle-stake-input"
            className="w-full px-4 py-3 rounded-xl bg-white/[0.06] border border-white/10 mb-1 outline-none focus:border-[#FFB42D]"/>
          <p className="text-xs text-white/40 mb-4">Your balance: {meta.me?.coin_balance ?? 0} coins · <Link to="/plyx/wallet" className="underline">buy coins</Link></p>
          <label className="block text-sm font-bold mb-2">Category</label>
          <div className="flex flex-wrap gap-2 mb-6">
            {meta.categories.map((c) => (
              <button key={c.code} type="button" onClick={() => setCategory(c.code)}
                data-testid={`millions-battle-cat-${c.code}`}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold ${category === c.code ? "bg-[#FFB42D] text-black" : "bg-white/10"}`}>
                {c.emoji} {c.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={createBattle} disabled={battleBusy}
            data-testid="millions-battle-create"
            className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-black font-extrabold disabled:opacity-50">
            {battleBusy ? "Creating…" : `Create battle · stake ${battleForm.stake} coins`}
          </button>
        </div>
      </div>
    );
  }

  // ───────────────────────── HUB ─────────────────────────
  return (
    <div className="min-h-screen millions-stage text-white pb-24" data-testid="millions-hub">
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <div className="flex items-center justify-between mb-1">
          <Link to="/plyx/feed" className="flex items-center gap-1 text-white/60 text-sm" data-testid="millions-back-feed">
            <ArrowLeft className="w-4 h-4"/> Feed
          </Link>
          {meta.me && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-sm font-bold" data-testid="millions-coin-balance">
              <Coins className="w-4 h-4 text-[#FFB42D]"/> {meta.me.coin_balance}
            </div>
          )}
        </div>
        <h1 className="text-3xl font-extrabold mt-3">Plyx <span className="text-[#FFB42D]">Millions</span> 💰</h1>
        <p className="text-sm text-white/60 mt-1 mb-5">8 Millionaire-standard questions per game — wrong answers don't end your run, your score out of 8 is what counts. Duel rivals or squad up for coin pots.</p>

        <GoldenHourBlock onPlay={() => startGame(null)} />

        <Link to="/plyx/games" className="inline-flex items-center gap-1.5 text-xs font-extrabold text-[#00E0C7] mb-5" data-testid="millions-all-games">
          🎮 All Plyx games — Brain Clash live rooms &amp; more →
        </Link>

        <div className="mb-5">
          <div className="text-sm font-bold mb-2">Pick your category</div>
          <div className="flex flex-wrap gap-2">
            {meta.categories.map((c) => (
              <button key={c.code} type="button" onClick={() => setCategory(c.code)}
                data-testid={`millions-cat-${c.code}`}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${category === c.code ? "bg-[#FFB42D] text-black" : "bg-white/10 hover:bg-white/15"}`}>
                {c.emoji} {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          <button type="button" onClick={() => startGame(null)}
            data-testid="millions-play-solo"
            className="rounded-2xl p-5 text-left bg-gradient-to-br from-[#FFB42D] to-[#FF2D7A] text-black">
            <Crown className="w-6 h-6 mb-2"/>
            <div className="font-extrabold text-lg leading-tight">Solo Climb</div>
            <div className="text-xs font-semibold opacity-80 mt-1">Free · play for points & glory</div>
          </button>
          <button type="button" onClick={() => { if (!user) { navigate("/plyx/login"); return; } setView("duel-new"); }}
            data-testid="millions-play-duel"
            className="rounded-2xl p-5 text-left bg-gradient-to-br from-[#00E0C7] to-[#1a7bff] text-black">
            <Swords className="w-6 h-6 mb-2"/>
            <div className="font-extrabold text-lg leading-tight">1v1 Duel</div>
            <div className="text-xs font-semibold opacity-80 mt-1">Stake coins · winner takes the pot</div>
          </button>
          <button type="button" onClick={() => { if (!user) { navigate("/plyx/login"); return; } setView("battle-new"); }}
            data-testid="millions-play-battle"
            className="col-span-2 rounded-2xl p-5 text-left bg-gradient-to-br from-[#FF2D7A] to-[#7b2dff] text-white flex items-center justify-between gap-3">
            <div>
              <Users className="w-6 h-6 mb-2"/>
              <div className="font-extrabold text-lg leading-tight">Team Battle</div>
              <div className="text-xs font-semibold opacity-80 mt-1">2v2 to 5v5 · combined scores win the pot · share a join link</div>
            </div>
            <span className="text-2xl">🔥</span>
          </button>
        </div>

        {duels.length > 0 && (
          <div className="mb-6" data-testid="millions-duel-list">
            <div className="text-sm font-bold mb-2 flex items-center gap-1.5"><Swords className="w-4 h-4 text-[#00E0C7]"/> Your duels</div>
            <div className="space-y-2">
              {duels.map((d) => {
                const other = d.i_am_challenger ? d.opponent : d.challenger;
                const canAccept = d.status === "pending" && !d.i_am_challenger;
                const canPlay = d.status === "active" && (!d.my_session || d.my_session.status === "active");
                const settled = d.status === "settled";
                const iWon = settled && d.winner_id && ((d.i_am_challenger && d.winner_id === d.challenger_id) || (!d.i_am_challenger && d.winner_id === d.opponent_id));
                return (
                  <div key={d.duel_id} className="rounded-xl bg-white/[0.05] border border-white/10 p-3 flex items-center justify-between gap-2" data-testid={`millions-duel-${d.duel_id}`}>
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate">vs @{other?.handle || "?"} · {d.stake_coins} coins</div>
                      <div className="text-xs text-white/50">
                        {d.status === "pending" && (d.i_am_challenger ? "Waiting for them to accept" : "They challenged you!")}
                        {d.status === "active" && (canPlay ? "Your climb is ready — play now" : "Waiting for opponent's climb")}
                        {settled && (d.winner_id ? (iWon ? `🏆 You won ${d.payout_coins} coins!` : "They out-climbed you this time") : "Tie — stakes refunded")}
                        {d.status === "declined" && "Declined — stake refunded"}
                      </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      {canAccept && (
                        <>
                          <button type="button" onClick={() => duelAction(d, "accept")} data-testid={`millions-accept-${d.duel_id}`}
                            className="px-3 py-1.5 rounded-full bg-[#00E0C7] text-black text-xs font-extrabold">Accept</button>
                          <button type="button" onClick={() => duelAction(d, "decline")} data-testid={`millions-decline-${d.duel_id}`}
                            className="px-3 py-1.5 rounded-full bg-white/10 text-xs font-bold">Decline</button>
                        </>
                      )}
                      {canPlay && (
                        <button type="button" onClick={() => startGame(d.duel_id)} data-testid={`millions-play-${d.duel_id}`}
                          className="px-3 py-1.5 rounded-full bg-[#FFB42D] text-black text-xs font-extrabold">Play</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {(battles.mine.length > 0 || battles.open.length > 0) && (
          <div className="mb-6" data-testid="millions-battle-list">
            <div className="text-sm font-bold mb-2 flex items-center gap-1.5"><Users className="w-4 h-4 text-[#FF2D7A]"/> Team battles</div>
            <div className="space-y-2">
              {battles.mine.map((b) => <BattleRow key={b.battle_id} b={b} mine />)}
              {battles.open.slice(0, 5).map((b) => <BattleRow key={b.battle_id} b={b} />)}
            </div>
          </div>
        )}

        <NationsBlock user={user} />

        <LeaderboardBlock />

        <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 text-xs text-white/50 leading-relaxed">
          <span className="font-bold text-white/80">How it works:</span> 15 questions, difficulty climbs with the money. Safe rungs at 5 and 10 protect your points. Lifelines: one 50:50 and one Skip per climb. In duels you each get different AI-generated questions in the same category — the higher climber takes the pot. Fans can gift 🌸 flowers and 💰 coins to any player's game via their share link.
        </div>
      </div>
    </div>
  );
}

const LeaderboardBlock = () => {
  const [leaders, setLeaders] = useState(null);
  useEffect(() => {
    api.get("/plyx/millions/leaderboard").then((r) => setLeaders(r.data?.leaders || [])).catch(() => setLeaders([]));
  }, []);
  if (!leaders || leaders.length === 0) return null;
  return (
    <div className="mb-6" data-testid="millions-leaderboard">
      <div className="text-sm font-bold mb-2 flex items-center gap-1.5"><Trophy className="w-4 h-4 text-[#FFB42D]"/> Top climbers</div>
      <div className="space-y-1.5">
        {leaders.slice(0, 5).map((l, i) => (
          <div key={l.plyx_user_id} className="flex items-center gap-3 rounded-xl bg-white/[0.04] px-3 py-2">
            <span className="w-5 text-center font-extrabold text-[#FFB42D]">{i + 1}</span>
            <span className="font-bold text-sm flex-1 truncate">@{l.handle}</span>
            <span className="text-xs text-white/60">{(l.millions_points || 0).toLocaleString()} pts</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const fmtCountdown = (secs) => {
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  return `${h > 0 ? `${h}h ` : ""}${m}m ${String(s).padStart(2, "0")}s`;
};

const GoldenHourBlock = ({ onPlay }) => {
  const [g, setG] = useState(null);
  const [secs, setSecs] = useState(0);
  const load = useCallback(() => {
    api.get("/plyx/millions/golden").then((r) => {
      setG(r.data);
      setSecs(Math.max(0, r.data.active ? r.data.ends_in_seconds : r.data.starts_in_seconds));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    const t = setInterval(() => setSecs((s) => { if (s <= 1) { load(); return 0; } return s - 1; }), 1000);
    return () => clearInterval(t);
  }, [load]);
  if (!g) return null;
  if (g.active) {
    return (
      <div className="mb-5 rounded-2xl border border-[#FFB42D]/50 bg-gradient-to-r from-[#FFB42D]/15 via-[#FF2D7A]/10 to-transparent p-4" data-testid="millions-golden-live">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="font-extrabold text-[#FFB42D] flex items-center gap-1.5">🌟 GOLDEN HOUR IS LIVE <span className="w-2 h-2 rounded-full bg-[#FF2D7A] animate-pulse"/></div>
            <div className="text-xs text-white/70 mt-0.5">2× points on every solo climb · top 3 split {g.prize_pool} coins · ends in <span className="font-bold text-white" data-testid="millions-golden-countdown">{fmtCountdown(secs)}</span></div>
            {g.my_best_rung != null && <div className="text-xs text-[#00E0C7] font-bold mt-0.5" data-testid="millions-golden-mybest">Your best tonight: {g.my_best_rung}/8</div>}
          </div>
          <button type="button" onClick={onPlay} data-testid="millions-golden-play"
            className="px-5 py-2 rounded-full bg-[#FFB42D] text-black text-sm font-extrabold shrink-0">Climb now</button>
        </div>
        {(g.leaderboard || []).length > 0 && (
          <div className="mt-3 space-y-1" data-testid="millions-golden-leaderboard">
            {g.leaderboard.slice(0, 5).map((row, i) => (
              <div key={row.player_id} className="flex items-center gap-2 text-xs">
                <span className="w-4 text-center font-extrabold text-[#FFB42D]">{i + 1}</span>
                <span className="font-bold flex-1 truncate">@{row.player?.handle}</span>
                <span className="text-white/60">score {row.rung}</span>
                {i < 3 && <span className="text-[#FFB42D] font-bold">{g.prizes[i]} 🪙</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="mb-5 rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="millions-golden-upcoming">
      <div className="font-extrabold flex items-center gap-1.5">🌟 Golden Hour <span className="text-xs font-bold text-white/50">daily · {g.window_label}</span></div>
      <div className="text-xs text-white/60 mt-0.5">2× points on every solo climb — top 3 split {g.prize_pool} coins. Starts in <span className="font-bold text-[#FFB42D]" data-testid="millions-golden-countdown">{fmtCountdown(secs)}</span></div>
      {g.last_result && (
        <div className="mt-2.5 flex items-center gap-2 flex-wrap text-xs" data-testid="millions-golden-last-winners">
          <span className="text-white/50 font-bold">Last night:</span>
          {g.last_result.winners.map((w) => (
            <span key={w.place} className="px-2 py-0.5 rounded-full bg-white/10 font-bold">
              {w.place === 1 ? "🥇" : w.place === 2 ? "🥈" : "🥉"} @{w.player?.handle} · {w.prize} 🪙
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const BattleRow = ({ b, mine }) => {
  const filled = b.team_a.members.length + b.team_b.members.length;
  const total = b.team_size * 2;
  return (
    <Link to={`/plyx/millions/battle/${b.battle_id}`} data-testid={`millions-battle-${b.battle_id}`}
      className="block rounded-xl bg-white/[0.05] border border-white/10 p-3 hover:border-[#FF2D7A]/50 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-bold text-sm truncate">{b.team_a.name} vs {b.team_b.name} · {b.stake_coins} coins each</div>
          <div className="text-xs text-white/50">
            {b.status === "open" && `${filled}/${total} players — ${mine ? "waiting for players, share the link!" : "open — tap to join"}`}
            {b.status === "active" && (b.my_session ? (b.my_session.status === "active" ? "LIVE — finish your climb!" : "Waiting for the other climbs") : mine ? "LIVE — play your climb now!" : "Battle in progress")}
            {b.status === "settled" && (b.winner_side ? `Settled — ${b.winner_side === "a" ? b.team_a.name : b.team_b.name} won the pot` : "Settled — tie, stakes refunded")}
            {b.status === "cancelled" && "Cancelled — stakes refunded"}
          </div>
        </div>
        <span className="text-[11px] font-extrabold text-[#FFB42D] shrink-0">{b.category_label}</span>
      </div>
    </Link>
  );
};

const NationsBlock = ({ user }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState("");
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    api.get("/plyx/millions/nations").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const save = async () => {
    if (!sel) { toast.error("Pick your country first"); return; }
    setSaving(true);
    try {
      await api.post("/plyx/millions/nation", { country_code: sel, region: "" });
      toast.success("Every climb you finish now scores for your flag!");
      setPicking(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };
  const showPicker = user && (picking || !data.my_country);
  return (
    <div className="mb-6" data-testid="millions-nations">
      <div className="text-sm font-bold mb-2 flex items-center gap-1.5"><Globe className="w-4 h-4 text-[#00E0C7]"/> Nations League · {data.month}</div>
      <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-3">
        <p className="text-xs text-white/50 mb-3">Every climb you finish adds your points to your country&apos;s monthly total. Top nation takes the crown when the month ends.</p>
        {showPicker ? (
          <div className="flex gap-2 mb-3">
            <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid="millions-country-select"
              className="flex-1 min-w-0 px-3 py-2 rounded-xl bg-[#151527] border border-white/10 text-sm outline-none">
              <option value="">Choose your country…</option>
              {(data.country_options || []).map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
              ))}
            </select>
            <button type="button" onClick={save} disabled={saving} data-testid="millions-country-save"
              className="px-4 py-2 rounded-xl bg-[#00E0C7] text-black text-sm font-extrabold disabled:opacity-50 shrink-0">Save</button>
          </div>
        ) : data.my_country ? (
          <div className="flex items-center justify-between mb-3 text-sm">
            <span data-testid="millions-my-country">Playing for <span className="font-extrabold">{data.my_country.flag} {data.my_country.name}</span></span>
            <button type="button" onClick={() => { setPicking(true); setSel(data.my_country.code); }} data-testid="millions-country-change"
              className="text-xs text-white/50 underline">change</button>
          </div>
        ) : null}
        {(data.countries || []).length === 0 ? (
          <p className="text-xs text-white/40" data-testid="millions-nations-empty">No climbs this month yet — be the first to score for your flag!</p>
        ) : (
          <div className="space-y-1.5">
            {data.countries.slice(0, 10).map((c, i) => (
              <div key={c.code} className="flex items-center gap-3 rounded-xl bg-white/[0.04] px-3 py-2" data-testid={`millions-nation-${c.code}`}>
                <span className="w-5 text-center font-extrabold text-[#00E0C7]">{i + 1}</span>
                <span className="text-lg leading-none">{c.flag}</span>
                <span className="font-bold text-sm flex-1 truncate">{c.name}</span>
                <span className="text-xs text-white/60">{c.points.toLocaleString()} pts · {c.players} player{c.players === 1 ? "" : "s"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export { GiftBar, Ladder };
